import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { StatCard } from "./Shared";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Briefcase, FileText, UserCheck, TrendingUp, Loader2, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

const WEIGHT_LABELS = { role: "Ruolo", distance: "Distanza", availability: "Disponibilità", experience: "Esperienza", contract: "Contratto", license: "Patente", vehicle: "Automunito" };
const ROLE_LABELS = { cameriere: "Cameriere", barista: "Barista", pizzaiolo: "Pizzaiolo", cuoco: "Cuoco", aiuto_cucina: "Aiuto cucina", rider: "Rider", cassiere: "Cassiere", commesso: "Commesso", altro: "Altro" };

export default function JobsCeoPanel() {
  const [stats, setStats] = useState(null);
  const [weights, setWeights] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/ceo/jobs/stats").then(({ data }) => setStats(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/ceo/jobs/weights").then(({ data }) => setWeights(data.weights)).catch(() => {});
  }, []);
  if (!stats) return <div className="text-zinc-400"><Loader2 className="h-4 w-4 animate-spin inline" /> Caricamento…</div>;
  const total = weights ? Object.values(weights).reduce((a, b) => a + b, 0) : 0;
  const saveWeights = async () => {
    setSaving(true);
    try { const { data } = await api.put("/ceo/jobs/weights", { weights }); setWeights(data.weights); toast.success("Pesi aggiornati"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const maxRole = Math.max(1, ...stats.top_roles.map((r) => r.count));
  return (
    <div className="space-y-6" data-testid="ceo-jobs-panel">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#00E676]/15 border border-[#00E676]/30 flex items-center justify-center"><Briefcase className="h-5 w-5 text-[#00E676]" /></div>
        <div><h2 className="font-display text-xl font-bold">Lavoro · Panoramica</h2><p className="text-sm text-zinc-400">Annunci, candidature e assunzioni sul territorio.</p></div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="ceojobs-posts" label="Annunci attivi" value={`${stats.posts_active}/${stats.posts_total}`} icon={Briefcase} accent />
        <StatCard testid="ceojobs-apps" label="Candidature" value={stats.applications_total} icon={FileText} />
        <StatCard testid="ceojobs-hires" label="Assunzioni" value={stats.hires_total} icon={UserCheck} accent={stats.hires_total > 0} />
        <StatCard testid="ceojobs-open" label="Posizioni aperte" value={stats.positions_open} icon={TrendingUp} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5">
          <h3 className="font-semibold mb-3">Categorie più richieste</h3>
          {stats.top_roles.length === 0 ? <div className="text-sm text-zinc-500">Nessun dato.</div> : stats.top_roles.map((r) => (
            <div key={r.role_type} className="mb-2">
              <div className="flex justify-between text-sm mb-0.5"><span>{ROLE_LABELS[r.role_type] || r.role_type}</span><span className="text-zinc-400">{r.count}</span></div>
              <div className="h-2 rounded-full bg-white/5"><div className="h-full rounded-full bg-[#00E676]" style={{ width: `${(r.count / maxRole) * 100}%` }} /></div>
            </div>
          ))}
        </div>

        <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5" data-testid="ceo-weights">
          <h3 className="font-semibold mb-1 flex items-center gap-2"><SlidersHorizontal className="h-4 w-4 text-[#00E676]" /> Pesi del matching AI</h3>
          <p className="text-xs text-zinc-500 mb-3">Configura l'importanza di ogni criterio (senza toccare il codice). Totale attuale: <b className={total === 100 ? "text-[#00E676]" : "text-amber-400"}>{total}</b></p>
          {weights && Object.keys(WEIGHT_LABELS).map((k) => (
            <div key={k} className="flex items-center gap-3 mb-2">
              <span className="text-sm w-28 text-zinc-300">{WEIGHT_LABELS[k]}</span>
              <input type="range" min="0" max="40" value={weights[k]} onChange={(e) => setWeights((w) => ({ ...w, [k]: parseInt(e.target.value) }))} className="flex-1 accent-[#00E676]" data-testid={`weight-${k}`} />
              <span className="text-sm w-8 text-right text-[#00E676] font-semibold">{weights[k]}</span>
            </div>
          ))}
          <Button onClick={saveWeights} disabled={saving} className="mt-2 bg-[#00E676] text-black w-full" data-testid="save-weights">{saving ? "Salvataggio…" : "Salva pesi"}</Button>
        </div>
      </div>
    </div>
  );
}
